import { ApiBase } from '@bymason/api-base';
import {
  FeatureFlag,
  UpdateUserPrefsOptions,
  UserPrefsResult,
  GetUserPrefsOptions,
  MFAEnrollment,
  MFAEnrollmentTicket,
} from './types';

export * from './types';

class User extends ApiBase {
  private static instance: User;

  private constructor() {
    super();
  }

  /**
   * There is no need for consumers to ever call this method as its purely used to enforce a singleton module pattern.
   * @returns An Account instance
   */
  public static getInstance(): User {
    if (!User.instance) {
      User.instance = new User();
      User.instance.configure({
        fleet: 'global',
      });
    }

    return User.instance;
  }

  /**
   * Get a list of feature flags enabled for the user
   * @param options metadata required to get feature-flags.
   * @param clientToken token required for proper authorization.
   * @returns either a list of feature flags or void
   */
  public async getFeatureFlags(clientToken: string) {
    const url = this.getEndpointTemplate(`user/feature-flags`);
    const dataResponse = await this.callJsonEndpoint<FeatureFlag[]>({
      clientToken,
      method: 'GET',
      url,
    });
    return dataResponse;
  }

  /**
   * Update user preferences
   * @param options metadata required to update preferences.
   * @param clientToken token required for proper authorization.
   * @returns either an account model or void
   */
  public async updateUserPrefs(options: UpdateUserPrefsOptions, clientToken: string) {
    const url = this.getEndpointTemplate(`user/preferences/${options.appId}`);
    const dataResponse = await this.callJsonEndpoint<UserPrefsResult>({
      body: JSON.stringify(options),
      clientToken,
      method: 'PATCH',
      url,
    });

    return dataResponse;
  }

  /**
   * Get user preferences
   * @param options metadata required to get prefs.
   * @param clientToken token required for proper authorization.
   * @returns either an preference model or void
   */
  public async getUserPrefs(options: GetUserPrefsOptions, clientToken: string) {
    const url = this.getEndpointTemplate(`user/preferences/${options.appId}`);
    const dataResponse = await this.callJsonEndpoint<UserPrefsResult>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  public async getUserMfa(clientToken: string) {
    const url = this.getEndpointTemplate(`user/mfa`);
    const dataResponse = await this.callJsonEndpoint<MFAEnrollment[]>({
      clientToken,
      method: 'GET',
      url,
    });

    return dataResponse;
  }

  public async enrollMfa(clientToken: string) {
    const url = this.getEndpointTemplate(`user/mfa/enroll`);
    const dataResponse = await this.callJsonEndpoint<MFAEnrollmentTicket>({
      clientToken,
      method: 'POST',
      url,
    });

    return dataResponse;
  }

  public async deleteMfaFactor(id: string, clientToken: string) {
    const url = this.getEndpointTemplate(`user/mfa/${id}`);
    const dataResponse = await this.callVoidEndpoint({
      clientToken,
      method: 'DELETE',
      url,
    });

    return dataResponse;
  }

  public async deleteAllMfaFactors(clientToken: string) {
    const url = this.getEndpointTemplate(`user/mfa`);
    const dataResponse = await this.callVoidEndpoint({
      clientToken,
      method: 'DELETE',
      url,
    });

    return dataResponse;
  }
}

export const userClient = User.getInstance();
